import { uniq } from 'lodash-es';
import helpers from '../../utils/helpers';
import { seriesLineWidth } from '../../utils/charts';
import { seriesColor, ratingsColor } from '../../pages/app-insights/review-analysis/shared/config';

const dashStyles = [
  'Solid',
  'ShortDash',
  'ShortDot',
  'ShortDashDot',
  'ShortDashDotDot',
  'Dot',
  'Dash',
  'LongDash',
  'DashDot',
  'LongDashDot',
  'LongDashDotDot',
];

const wwColor = '#3B4355';
const highlightColor = '#48268A';
const negativeColor = '#E33963';
const lineColors = [
  '#0B4FBA',
  '#8EEDE6',
  '#CDABF9',
  '#A90051',
  '#F24979',
  '#008A45',
  '#99C1FF',
  '#3382FF',
  '#A8F4CE',
  '#FFBDA0',
  '#CF4112',
  '#1FCC75',
  '#9B57F2',
  '#00E1D4',
  '#FF7337',
  '#04757C',
  '#FEB0C6',
  '#CED5E0',
  '#8E95A7',
  '#3B4355',
  '#c2c8d3',
  '#4DD0E1',
  '#15CFD4',
];

const extraColors = [
  '#348BE3',
  '#45B068',
  '#3E8EDE',
  '#A9CEF2',
  '#A1CDF7',
  '#AFE09D',
  '#E56389',
  '#E1E5EB',
  '#BEC9DB',
  '#EA5782',
  '#F7ABB7',
  '#A26CBD',
  '#CCB0EB',
  '#F57F51',
  '#FFB296',
  '#8AE7EA',
  '#748AB2',
  '#FCC047',
  '#FCD78B',
  '#EDF5FD',
  '#E3FCFC',
  '#FFF2ED',
  '#F6F0F9',
  '#FDECF0',
  '#F5F7FA',
];

const colorsCollection = uniq([
  highlightColor,
  negativeColor,
  ...lineColors,
  ...extraColors,
  ...Object.values({ ...seriesColor, ...ratingsColor }),
]);

const DefaultOptions = {
  colors: lineColors,
  chart: {
    // Fix needed for the touchpad fingers spreading and autozooming
    // normal select-to-zooming still works
    pinchType: '',
    zooming: {
      mouseWheel: {
        enabled: false,
      },
    },
    panning: {
      enabled: true,
      type: 'x',
    },
    style: {
      fontFamily: 'Lato, sans-serif',
    },
    resetZoomButton: {
      theme: {
        style: {
          fontSize: '11px',
        },
      },
    },
    //  type: 'spline',
  },
  credits: {
    enabled: false,
  },
  accessibility: {
    enabled: false,
  },
  // Highcharts 12 sizes the scrollbar thumb as `height - trackBorderWidth`, so the
  // `height: 0` charts use to hide the scrollbar renders a `<rect height="-1">`.
  // Highcharts 13 also puts a 3px margin above a navigator's scrollbar; 10.3.3 forced 0.
  scrollbar: {
    trackBorderWidth: 0,
    margin: 0,
  },
  palette: {
    colorScheme: 'light',
  },
  legend: {
    align: 'right',
    verticalAlign: 'top',
    layout: 'vertical',
    itemStyle: {
      fontWeight: 'normal',
      fontSize: '1.1rem',
    },
    events: {
      itemClick() {
        return false;
      },
    },
  },
  lang: {
    locale: 'en-US',
    noData: 'No data to display',
    thousandsSep: ',',
    numericSymbols: ['K', 'M', 'G', 'T', 'P', 'E'],
    loading: 'Loading Data...',
  },
  title: {
    minScale: 1,
    style: {
      fontSize: '12px',
    },
  },
  pane: {
    size: '85%',
    center: ['50%', '50%'],
    startAngle: 0,
    endAngle: 360,
    background: {
      backgroundColor: '#ffffff',
    },
  },
  plotOptions: {
    series: {
      nullColor: '#ffffff',
      borderRadius: 0,
      states: {
        hover: {
          enabled: false,
        },
        inactive: {
          enabled: false,
        },
      },
      lineWidth: seriesLineWidth,
      dataLabels: {
        style: {
          fontWeight: 'normal',
        },
      },
      marker: {
        enabled: false,
      },
    },
    areaspline: {
      lineWidth: 0,
    },
    solidgauge: {
      borderRadius: 0,
    },
  },
  tooltip: {
    padding: 1,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderColor: 'rgba(59, 67, 85, 0.18)',
    borderRadius: 8,
    style: {
      color: '#555F77',
      padding: '0',
      fontSize: '12px',
    },
    shadow: false,
    useHTML: true,
    crosshairs: {
      width: 1,
      color: '#CED2DB',
    },
    shared: true,
    dateTimeLabelFormats: {
      day: '%b %e, %Y',
    },
    headerFormat: '<div class="m-b-xxs text-bold">{point.key}</div><table class="table">',
    pointFormat: `
      <tr>
        <td class="text-xxs-left p-l-0">
          <span style="color:{series.color};">\u25CF</span> {series.name}
        </td>
        <td class="text-xxs-right p-r-0"><strong>{series.options.units}{point.y}</strong>
        </td>
      </tr>`,
    footerFormat: '</table>',
    positioner(lw, lh, p) {
      const px = p.plotX;
      const py = p.plotY;
      const pw = this.chart.plotWidth;
      const ph = this.chart.plotHeight;
      const pl = this.chart.plotLeft;
      const pt = this.chart.plotTop;
      const tx = px + lw > pw ? px + pl - lw - 20 : px + pl + 20;
      const ty = py + pt - lh / 2 < 0 ? 20 : py + lh > ph ? ph - lh + 10 : py;
      return {
        x: tx,
        y: ty,
      };
    },
  },
  defs: {
    patterns: colorsCollection
      .map((hexColor) => ({
        id: `custom-pattern-${hexColor}-column`,
        path: {
          d: 'M 0 10 L 10 0 M -1 1 L 1 -1 M 9 11 L 11 9',
          stroke: helpers.hexToRGB(hexColor, 0.4),
          strokeWidth: 3,
        },
      }))
      .concat(
        colorsCollection.map((hexColor) => ({
          id: `custom-pattern-${hexColor}-areaspline`,
          path: {
            d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
            stroke: helpers.hexToRGB(hexColor, 0.4),
            strokeWidth: 3,
          },
        }))
      )
      .concat(
        Object.values(helpers.storeColors).map((hexColor) => ({
          id: `custom-pattern-${hexColor}-column`,
          path: {
            d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
            stroke: helpers.hexToRGB(hexColor, 0.4),
            strokeWidth: 3,
          },
        }))
      )
      .concat(
        Object.values(helpers.storeColors).map((hexColor) => ({
          id: `custom-pattern-${hexColor}-areaspline`,
          path: {
            d: 'M 0 10 L 10 0 M -1 1 L 1 -1 M 9 11 L 11 9',
            stroke: helpers.hexToRGB(hexColor, 0.4),
            strokeWidth: 3,
          },
        }))
      ),
  },
  yAxis: {
    gridLineColor: 'transparent',
    lineWidth: 0,
    lineColor: '#E1E5EB',
    startOnTick: false,
    minRange: 0.1,
    labels: {
      style: {
        color: '#848EA1',
        fontSize: '11px',
      },
    },
  },
  xAxis: {
    lineColor: '#E1E5EB',
    tickColor: '#E1E5EB ',
    labels: {
      dateFormat: '{value:%b %e}',
      // 10.3.3 kept x labels 8px off the plot (v12+: 15); bar charts reset x to 15 and y to 8.
      distance: 8,
      style: {
        color: '#848EA1',
        fontSize: '11px',
      },
    },
  },
  exporting: {
    enabled: false,
  },
  loading: {
    labelStyle: {
      color: '#555f77',
      fontSize: '14px',
    },
    style: {
      opacity: '1',
    },
  },
  noData: {
    position: {
      align: 'center',
      verticalAlign: 'middle',
    },
    style: {
      color: '#5D6B85',
      fontSize: '16px',
      fontWeight: 'normal',
    },
  },
  navigation: {
    buttonOptions: {
      enabled: false,
    },
  },
};

export default DefaultOptions;
export { lineColors, dashStyles, wwColor, highlightColor };
